import { Controller } from "@hotwired/stimulus"

// Chip/token multi-select inside a column-filter popover
// (Cup::DataTable::TokenSelect). Selecting a value never touches the server:
// the component renders every option twice (a chip and a list item) and this
// controller only flips which of the two is visible, enabling/disabling the
// chip's hidden input so the surrounding GET form carries exactly the chosen
// values. The popover therefore survives an entire editing session and costs
// one request on Apply.
export default class extends Controller {
  static targets = ["chip", "option", "input", "empty", "dropdown"]
  static values = { attribute: String }

  connect() {
    this.highlightIndex = 0
    this.refresh()
  }

  focus() {
    this.inputTarget.focus()
  }

  // The options are a dropdown, not a permanent list: the popover opens showing
  // just the chip box, and the list drops open once that box takes focus — so an
  // untouched multi filter looks like the text filter's, one input and nothing
  // else.
  //
  // Two deliberate constraints, both learned the hard way:
  //
  // - it GROWS the popover instead of floating over it. Floated, it would cover
  //   the Apply button right below, and a click meant for Apply would land on
  //   whichever option sat under the cursor.
  // - it does NOT close on outside-click. Closing on mousedown collapses the
  //   list, the footer jumps up by the list's height, and the mouseup then
  //   lands where Apply no longer is — the click is swallowed. Esc closes it;
  //   otherwise it lives as long as the popover does.
  open() {
    this.dropdownTarget.classList.remove("hidden")
  }

  close() {
    this.dropdownTarget.classList.add("hidden")
  }

  isOpen() {
    return !this.dropdownTarget.classList.contains("hidden")
  }

  // Clicking the chip box (but not a chip's × ) puts the caret in the input,
  // the way a native token field behaves.
  focusInput(event) {
    if (event.target.closest("button")) return
    this.focus()
  }

  select(event) {
    this.take(event.currentTarget.dataset.value)
  }

  remove(event) {
    event.preventDefault()
    this.setSelected(event.currentTarget.dataset.value, false)
    this.refresh()
    this.focus()
  }

  filter() {
    this.highlightIndex = 0
    this.open()
    this.refresh()
  }

  // Restores a previous selection wholesale — column_filter_controller#cancel
  // calls this with the chips that were applied when the popover opened.
  setValues(values) {
    const wanted = new Set(Array.from(values, String))
    this.chipTargets.forEach((chip) => this.setSelected(chip.dataset.value, wanted.has(chip.dataset.value)))
    this.inputTarget.value = ""
    this.highlightIndex = 0
    this.refresh()
  }

  keydown(event) {
    switch (event.key) {
      case "Enter": {
        event.preventDefault()
        const option = this.visibleOptions()[this.highlightIndex]
        if (option) this.take(option.dataset.value)
        break
      }
      // A first Esc dismisses the dropdown; only a second one reaches
      // column-filter and closes the whole popover.
      case "Escape":
        if (!this.isOpen()) break
        event.stopPropagation()
        this.close()
        break
      case "ArrowDown":
        event.preventDefault()
        this.open()
        this.moveHighlight(1)
        break
      case "ArrowUp":
        event.preventDefault()
        this.moveHighlight(-1)
        break
      case "Backspace": {
        if (this.inputTarget.value !== "") break
        const last = this.selectedChips().pop()
        if (!last) break
        event.preventDefault()
        this.setSelected(last.dataset.value, false)
        this.refresh()
        break
      }
    }
  }

  take(value) {
    this.setSelected(value, true)
    this.inputTarget.value = ""
    this.highlightIndex = 0
    this.refresh()
    this.focus()
  }

  setSelected(value, selected) {
    const chip = this.chipTargets.find((element) => element.dataset.value === value)
    const option = this.optionTargets.find((element) => element.dataset.value === value)
    if (!chip || !option) return

    chip.classList.toggle("hidden", !selected)
    chip.querySelector("input[type='hidden']").disabled = !selected
    option.dataset.selected = selected ? "true" : "false"
  }

  moveHighlight(delta) {
    const options = this.visibleOptions()
    if (options.length === 0) return

    this.highlightIndex = (this.highlightIndex + delta + options.length) % options.length
    this.applyHighlight(options)
  }

  // Option visibility has a single owner: an option is selectable when it is not
  // already a chip and its label matches the typeahead query.
  refresh() {
    const query = this.inputTarget.value.trim().toLowerCase()
    let matches = 0

    this.optionTargets.forEach((option) => {
      const selectable =
        option.dataset.selected !== "true" && (query === "" || (option.dataset.label || "").includes(query))
      option.classList.toggle("hidden", !selectable)
      if (selectable) matches++
    })

    if (this.hasEmptyTarget) this.emptyTarget.classList.toggle("hidden", matches > 0)
    this.applyHighlight(this.visibleOptions())
  }

  applyHighlight(options) {
    if (this.highlightIndex >= options.length) this.highlightIndex = 0

    options.forEach((option, index) => {
      const highlighted = index === this.highlightIndex
      option.classList.toggle("bg-zinc-100", highlighted)
      option.classList.toggle("dark:bg-zinc-800", highlighted)
      option.setAttribute("aria-selected", highlighted ? "true" : "false")
    })
  }

  selectedChips() {
    return this.chipTargets.filter((chip) => !chip.classList.contains("hidden"))
  }

  visibleOptions() {
    return this.optionTargets.filter((option) => !option.classList.contains("hidden"))
  }
}
